import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import ConfirmDialog from 'shared/ConfirmDialog';
import IconButtonWithTooltip from 'shared/IconButtonWithTooltip';

interface IEditSaveControlsProps {
  onSave: () => void;
  onCancelEdit: () => void;
  isCreateMode?: boolean;
  isHidden?: boolean;
  isDisabled?: boolean;
  isSubmitButtonHidden?: boolean;
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
}

const EditSaveControls: React.FC<IEditSaveControlsProps> = ({
  onSave, onCancelEdit, isCreateMode, isHidden, isEditMode,
  setIsEditMode, isDisabled, isSubmitButtonHidden,
}) => {
  const { dirty, isSubmitting } = useFormikContext();
  const withLeaveConfirmation = dirty && !isSubmitting;

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const toggleToEditMode = () => {
    setIsEditMode(true);
  };

  const onCancelConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
  };

  const onConfirmDialogConfirm = () => {
    setIsConfirmDialogOpen(false);
    setIsEditMode(false);
    onCancelEdit();
  };

  const toggleToViewMode = () => {
    if (withLeaveConfirmation) {
      setIsConfirmDialogOpen(true);
    } else {
      onConfirmDialogConfirm();
    }
  };

  if (isHidden) {
    return null;
  }

  if (isCreateMode) {
    if (isSubmitButtonHidden) {
      return null;
    }
    return (
      <IconButtonWithTooltip
        title="Save"
        onClick={onSave}
        disabled={isDisabled as boolean}
      >
        <SaveIcon />
      </IconButtonWithTooltip>
    );
  }

  if (!isEditMode) {
    return (
      <IconButtonWithTooltip
        title="Edit"
        onClick={toggleToEditMode}
        disabled={isDisabled as boolean}
      >
        <div className="edit-button__container">
          <EditOutlinedIcon />
          Edit
        </div>
      </IconButtonWithTooltip>
    );
  }

  return (
    <>
      {
        !isSubmitButtonHidden && (
          <IconButtonWithTooltip
            title="Save"
            onClick={onSave}
            disabled={isDisabled as boolean}
          >
            <SaveIcon />
          </IconButtonWithTooltip>
        )
      }
      <IconButtonWithTooltip
        title="Cancel"
        onClick={toggleToViewMode}
        disabled={isDisabled as boolean}
      >
        <ClearIcon />
      </IconButtonWithTooltip>
      <ConfirmDialog
        confirmLabel="Leave"
        visible={isConfirmDialogOpen}
        onCancel={onCancelConfirmDialog}
        onConfirm={onConfirmDialogConfirm}
      />
    </>
  );
};

EditSaveControls.defaultProps = {
  isHidden: false,
  isCreateMode: false,
  isDisabled: false,
  isSubmitButtonHidden: false,
};

export default EditSaveControls;
